import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gift, Copy, Mail, MessageSquare, Check } from "lucide-react";
import { toast } from "sonner";

export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/me/referral")
      .then((r) => setData(r.data))
      .catch(() => { /* silent */ });
  }, []);

  if (!data) return null;

  const shareLink = `${window.location.origin}/signup?ref=${data.code}`;
  const shareText = `I've been using Hope Chest to keep track of caregiving for my loved one — it's been such a calming difference. Want to try it? Sign up with my link and we both get 30 days of Premium free: ${shareLink}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Could not copy — please copy manually");
    }
  };

  const shareEmail = () => {
    const subject = encodeURIComponent("I'd love for you to try Hope Chest with me");
    const body = encodeURIComponent(shareText);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareSms = () => {
    const body = encodeURIComponent(shareText);
    window.location.href = `sms:?&body=${body}`;
  };

  return (
    <div className="hc-card p-6 mb-6 bg-gradient-to-br from-[#FDF6EC] to-[#F3EFEA] border border-[#E8DCC8]" data-testid="referral-card">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-2xl bg-[#3A5A4A] flex items-center justify-center flex-shrink-0">
          <Gift className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-xl">Refer a fellow caregiver</h3>
          <p className="text-sm text-[#7A736E] mt-1">
            Share Hope Chest with another caregiver. When they sign up, <strong>you both get {data.bonus_days_per_referral} days of Premium free</strong>.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-3 mb-3 flex items-center gap-2 border border-[#EBE5DF]">
        <code className="flex-1 text-sm text-[#3D3935] truncate font-mono" data-testid="referral-link">{shareLink}</code>
        <button
          onClick={copyLink}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#3A5A4A] hover:bg-[#2C4538] text-white text-xs flex-shrink-0 transition"
          data-testid="referral-copy"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button
          onClick={shareEmail}
          variant="outline"
          className="rounded-full border-[#EBE5DF] hover:bg-white text-sm"
          data-testid="referral-share-email"
        >
          <Mail className="w-4 h-4 mr-1.5" /> Email
        </Button>
        <Button
          onClick={shareSms}
          variant="outline"
          className="rounded-full border-[#EBE5DF] hover:bg-white text-sm"
          data-testid="referral-share-sms"
        >
          <MessageSquare className="w-4 h-4 mr-1.5" /> Text
        </Button>
      </div>

      {(data.friends_referred > 0 || data.days_left > 0) && (
        <div className="mt-5 pt-4 border-t border-[#E8DCC8] flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {data.friends_referred > 0 && (
            <div data-testid="referral-stats-friends">
              <span className="text-[#7A736E]">Friends referred</span>
              <p className="font-display text-2xl text-[#3A5A4A]">{data.friends_referred}</p>
            </div>
          )}
          {data.months_earned > 0 && (
            <div data-testid="referral-stats-months">
              <span className="text-[#7A736E]">Months earned</span>
              <p className="font-display text-2xl text-[#3A5A4A]">{data.months_earned}</p>
            </div>
          )}
          {data.days_left > 0 && (
            <div data-testid="referral-stats-days">
              <span className="text-[#7A736E]">Premium days left</span>
              <p className="font-display text-2xl text-[#3A5A4A]">{data.days_left}</p>
            </div>
          )}
        </div>
      )}

      {data.referred_users && data.referred_users.length > 0 && (
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer text-[#7A736E] hover:text-[#3D3935]" data-testid="referral-list-toggle">
            See who you&apos;ve invited ({data.referred_users.length})
          </summary>
          <ul className="mt-2 space-y-1 text-xs text-[#7A736E]">
            {data.referred_users.map((u, i) => (
              <li key={i} className="flex justify-between" data-testid={`referral-friend-${i}`}>
                <span>{u.name || u.email_masked}</span>
                <span>{new Date(u.joined_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
